import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Assignment as RequestIcon,
  Place as SiteIcon,
  MoreVert as MoreVertIcon, // CHANGE: Added MoreVert icon for dropdown
} from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';
import * as XLSX from 'xlsx';
import EditSite from "./EditSite";
import { apiCall } from "../../../services/ApiCall";
import config from "../../../config/config.json";
import Loader from "../../CompanyFolder/CompanyList/Loader";
import ViewSiteDetailsPage from "./ViewPage";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CanWrite, CanAddSiteContact } from "../../../context/AuthContext";

const Main = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // CHANGE: Added dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const open = Boolean(anchorEl);

  // State management (keeping original logic)
  const [loading, setLoading] = useState(false);
  const [deletedMode, setDeletedMode] = useState("DELETE");
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Modal states
  const [deleteId, setDeleteId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [mode, setMode] = useState(null);
  const [editData, setEditData] = useState(null);
  const [showAddDetails, setShowAddDetails] = useState(false);

  // Contact form states
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([{
    fname: "",
    lname: "",
    email: "",
    phone: "",
    designation: ""
  }]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const primaryColor = "#0d6efd";

  // CHANGE: Added dropdown menu handlers
  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  // Original functions (unchanged logic)
  const handleChange = (index, key, value) => {
    const updatedContacts = contacts.map((contact, i) =>
      i === index ? { ...contact, [key]: value } : contact
    );
    setContacts(updatedContacts);
  };

  const addNewContact = () => {
    setContacts([...contacts, { fname: "", lname: "", email: "", phone: "", designation: "" }]);
  };

  const removeContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    setContacts(updatedContacts);
  };

  const validate = () => {
    const newErrors = {};
    contacts.forEach((contact, index) => {
      const fname = contact.fname || "";
      const email = contact.email || "";
      const designation = contact.designation || "";

      if (!fname.trim()) {
        newErrors[`fname-${index}`] = "First Name is required";
      }
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
        newErrors[`email-${index}`] = "A valid Email is required";
      }
      if (!designation.trim()) {
        newErrors[`designation-${index}`] = "Designation is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addNewCompanyAndContact = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const { isSuccess } = await apiCall(
        `${config.POST_LOGIN_API_URL}addNewSitesContact`,
        {},
        { userData: contacts, siteId: Number(viewData.id) },
        "POST"
      );
      if (isSuccess) {
        getSitesList();
        setShowAddDetails(false);
        setContacts([{ fname: "", lname: "", email: "", phone: "", designation: "" }]);
        setSnackbar({ open: true, message: "Contact added successfully!", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to add contact", severity: "error" });
    }
  };

  const handleRoutingChange = (newPage, data) => {
    if (newPage === "addSite") {
      navigate("/add-Site");
    } else if (newPage === "viewRequest") {
      navigate(`/all-request/view/${data.id}`);
    } else if (newPage === "view") {
      setViewData(data);
      setShowDetails(true);
      setMode('VIEW');
    } else if (newPage === "edit") {
      setMode('EDIT');
      setEditData(data);
      setShowDetails(true);
    }
  };

  const handleActionIconClick = (id, mode) => {
    setDeleteId(id);
    setShowDeleteModal(true);
    setDeletedMode(mode);
  };

  const handleAction = async () => {
    try {
      await actionSite(deleteId);
      setShowDeleteModal(false);
      setSnackbar({
        open: true,
        message: `Site ${deletedMode.toLowerCase()}d successfully!`,
        severity: "success"
      });
    } catch (error) {
      setSnackbar({ open: true, message: "Action failed", severity: "error" });
    }
  };

  const actionSite = async (id) => {
    const endpoint = deletedMode === "DELETE" ? "deleteSite" : "archiveSite";
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}${endpoint}`,
      {},
      { siteId: id },
      "POST"
    );
    if (isSuccess) {
      getSitesList();
    }
  };

  const getSitesList = async () => {
    setLoading(true);
    const match = location.pathname.match(/\/site-list\/view\/(\d+)/);
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesList`,
      {},
      { companyId: match && match[1] },
      "GET"
    );
    setLoading(false);
    if (isSuccess) {
      setData(data?.data?.reverse() || []);
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(site => ({
        'Site ID': site?.id,
        "ARA ID": site?.araId,
        "Company Name": site?.companyName,
        'Site Address': `${site?.address}, ${site?.city}, ${site?.state}, ${site?.zipcode}`,
        "Created Date": site?.createdAt ? new Date(site.createdAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).replace(/\//g, '-') : '',
      }))
    );

    worksheet['!cols'] = [
      { wch: 10 }, { wch: 15 }, { wch: 40 }, { wch: 50 }, { wch: 15 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sites");
    XLSX.writeFile(workbook, "Sites.xlsx");
  };

  // Filter data based on search query
  const filteredData = data.filter((row) => {
    return [
      "id",
      "araId",
      "companyName",
      "name",
      "street",
      "city",
      "state",
      "zipcode",
      "address",
    ].some((key) =>
      row[key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // CHANGE: Updated DataGrid columns configuration with dropdown menu and black header colors
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'araId',
      headerName: 'ARA ID',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'start',
          height: '100%'
        }}>
          <BusinessIcon sx={{ color: primaryColor, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }} title={params.value}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'start',
          height: '100%'
        }}>
          <LocationIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary" title={params.value}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: `${primaryColor}15`,
            color: primaryColor,
            fontWeight: 500
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        // CHANGE: Replaced action buttons with dropdown menu
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row.id)}
          sx={{ color: 'text.secondary' }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Original useEffect hooks (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getSitesList();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else if (!showDetails && mode === 'EDIT') {
      getSitesList();
    }
  }, [showDetails]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* CHANGE: Updated Header Section - Moved buttons to search level */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{
            fontWeight: 600,
            color: 'black',
            mb: 1,
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}>
            Sites
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your site directory
          </Typography>
        </Box>

        {/* CHANGE: Updated Controls Row - Added buttons alongside search */}
        <Grid container spacing={2} alignItems="center">
          {/* Show Entries */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Show
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={paginationModel.pageSize}
                  onChange={(e) => setPaginationModel({
                    ...paginationModel,
                    pageSize: Number(e.target.value),
                    page: 0
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                entries
              </Typography>
            </Box>
          </Grid>

          {/* Search Box */}
          <Grid item xs={12} sm={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by ID, ARA ID, Company, Address, City, State..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>

          {/* Export and Add Site buttons 12 12 3*/}
          <Grid item xs={12} sm={12} md={3}> 
            <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportToExcel}
                size="medium"
                sx={{
                  borderColor: '#a3a3a3ff',
                  color: '#696868ff',
                  '&:hover': {
                    backgroundColor: `${'black'}10`,
                    borderColor: '#a3a3a3ff',
                  }
                }}
              >
                Export
              </Button>


                <CanWrite>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleRoutingChange("addSite")}
                size="medium"
                sx={{
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9,
                  }
                }}
              >
                Add Site
              </Button>
              </CanWrite>
            </Stack>
          </Grid>

          {/* Results Info */}
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Typography variant="body2" color="text.secondary">
                {filteredData.length} sites found
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/*  Data Grid with black header colors */}
      <Paper elevation={1} sx={{ height: 600, borderRadius: 2, display: { xs: 'none', md: 'block' } }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${primaryColor}08`,
              borderBottom: `2px solid ${primaryColor}20`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: 'black',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${primaryColor}05`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Paper>

      {/* Dropdown Menu for Actions */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          handleRoutingChange("view", selectedRow);
          handleMenuClose();
        }}>
          <ListItemText primary="View Details" />
        </MenuItem>

        <CanWrite>
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          handleRoutingChange("edit", selectedRow);
          handleMenuClose();
        }}>
          <ListItemText primary="Edit Site" />
        </MenuItem>
        </CanWrite>

          <CanAddSiteContact>
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          setShowAddDetails(true);
          setViewData(selectedRow);
          handleMenuClose();
        }}>
          <ListItemText primary="Add Contact" />
        </MenuItem>
        </CanAddSiteContact>

          <CanWrite>
        <MenuItem onClick={() => {
          handleActionIconClick(selectedRowId, "ARCHIVE");
          handleMenuClose();
        }}>
          <ListItemText primary="Archive" />
        </MenuItem>
        </CanWrite>

        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          handleRoutingChange("viewRequest", selectedRow);
          handleMenuClose();
        }}>
          <ListItemText primary="Requests" />
        </MenuItem>
      </Menu>

      {/* Mobile Cards Section */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Mobile Cards */}
            {filteredData
              .slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              )
              .map((row) => (
                <Card 
                  key={row.id} 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden'
                  }}
                >
                  {/* Card Header */}
                  <CardHeader
                    sx={{ 
                      backgroundColor: `${primaryColor}08`,
                      pb: 1,
                      pt: 2
                    }}
                    title={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600, fontSize: '16px' }}>
                            ID: {row?.id}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />

                  <CardContent sx={{ pt: 1, pb: 1 }}>
                    {/* Dynamic Content based on columns */}
                    {columns
                      .filter(col => col.field !== 'id' && col.field !== 'actions')
                      .map((column) => (
                        <Box key={column.field} sx={{ mb: 1.5 }}>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary', 
                            fontSize: '11px', 
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {column.headerName}:
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {column.renderCell ? (
                              column.renderCell({ row, value: row[column.field] })
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                {row[column.field] || '-'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}

                    {/* mobile actions to dropdown */}
                    <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2, mt: 2 }}>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '11px', 
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1,
                        display: 'block'
                      }}>
                        Actions:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, row.id)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

            {/* Mobile Pagination */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              mt: 3, 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <IconButton
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel({ 
                  ...paginationModel, 
                  page: paginationModel.page - 1 
                })}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:disabled': {
                    borderColor: '#f0f0f0'
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Typography variant="body2" sx={{ px: 2, minWidth: 'fit-content' }}>
                Page {paginationModel.page + 1} of {Math.ceil(filteredData.length / paginationModel.pageSize)}
              </Typography>

              <IconButton
                disabled={paginationModel.page >= Math.ceil(filteredData.length / paginationModel.pageSize) - 1}
                onClick={() => setPaginationModel({ 
                  ...paginationModel, 
                  page: paginationModel.page + 1 
                })}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:disabled': {
                    borderColor: '#f0f0f0'
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Mobile Results Info */}
            <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {Math.min((paginationModel.page + 1) * paginationModel.pageSize, filteredData.length)} of {filteredData.length} results
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Add Contact Modal */}
      <Dialog open={showAddDetails} onClose={() => setShowAddDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontSize: '1.5rem', fontWeight: 600 }}>
          Add Site Contact
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {contacts.map((contact, index) => (
              <Card key={index} sx={{ mb: 3, border: `1px solid ${primaryColor}20` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: primaryColor }}>
                      Contact {index + 1}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addNewContact}
                        sx={{ borderColor: primaryColor, color: primaryColor }}
                      >
                        Add
                      </Button>
                      {contacts.length > 1 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeContact(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={contact.fname}
                        onChange={(e) => handleChange(index, "fname", e.target.value)}
                        error={!!errors[`fname-${index}`]}
                        helperText={errors[`fname-${index}`]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={contact.lname}
                        onChange={(e) => handleChange(index, "lname", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleChange(index, "email", e.target.value)}
                        error={!!errors[`email-${index}`]}
                        helperText={errors[`email-${index}`]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                          Phone
                        </Typography>
                        <PhoneInput
                          country={'us'}
                          value={contact.phone}
                          onChange={(phone) => handleChange(index, 'phone', phone)}
                          inputStyle={{
                            width: '100%',
                            height: '56px',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!errors[`designation-${index}`]}>
                        <InputLabel>Designation</InputLabel>
                        <Select
                          value={contact.designation}
                          label="Designation"
                          onChange={(e) => handleChange(index, "designation", e.target.value)}
                        >
                          <MenuItem value="Site Admin">Site Admin</MenuItem>
                          <MenuItem value="Site Manager">Site Manager</MenuItem>
                        </Select>
                        {errors[`designation-${index}`] && (
                          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            {errors[`designation-${index}`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDetails(false)}>
            Cancel
          </Button>
          <Button
            onClick={addNewCompanyAndContact}
            variant="contained"
            sx={{ backgroundColor: primaryColor }}
          >
            Save Contacts
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete/Archive Confirmation Dialog */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>
          Confirm {deletedMode === "DELETE" ? "Delete" : "Archive"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {deletedMode === "DELETE" ? "delete" : "archive"} this site?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button
            onClick={handleAction}
            color={deletedMode === "DELETE" ? "error" : "warning"}
            variant="contained"
          >
            {deletedMode === "DELETE" ? "Delete" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Edit Modal */}
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontSize: '1.5rem', fontWeight: 600 }}>
          {mode === 'VIEW' ? 'Site Details' : 'Edit Site'}
        </DialogTitle>
        <DialogContent>
          {mode === 'VIEW' ?
            <ViewSiteDetailsPage viewData={viewData} /> :
            <EditSite editData={editData} setShowDetails={setShowDetails} />
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)} sx={{ color: primaryColor }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Main;
